import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  deleteCourseSession,
  listAllCourseSessions,
  updateCourseSession,
  type SessionFilters,
} from '../services/adminCourseSessionsApi';
import type { CourseSessionRow } from '../services/sessionsApi';
import { getAllUsers, type AdminUserRow } from '../services/usersApi';
import { ContentPageLayout } from '../components/Shared';

const SESSION_TYPES = ['regular', 'makeup', 'trial', 'workshop'];
const ATTENDANCE = ['present', 'late', 'absent', 'excused'];
const STATUSES = ['draft', 'completed'];
const USER_ROLES = [
  { value: 'any', label: 'Student or teacher' },
  { value: 'student', label: 'As student' },
  { value: 'teacher', label: 'As teacher' },
] as const;

type UserRoleFilter = (typeof USER_ROLES)[number]['value'];

const EMPTY_FILTERS = {
  status: '',
  attendanceStatus: '',
  sessionType: '',
  startDate: '',
  endDate: '',
  userId: '',
  userRole: 'any' as UserRoleFilter,
};

function userDisplayName(u: AdminUserRow) {
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return full || u.nickname || u.email;
}

function buildListFilters(
  page: number,
  filters: typeof EMPTY_FILTERS
): SessionFilters {
  const query: SessionFilters = {
    page,
    limit: 25,
    status: filters.status || undefined,
    attendanceStatus: filters.attendanceStatus || undefined,
    sessionType: filters.sessionType || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  };

  if (filters.userId) {
    if (filters.userRole === 'student') {
      query.studentId = filters.userId;
    } else if (filters.userRole === 'teacher') {
      query.teacherId = filters.userId;
    } else {
      query.userId = filters.userId;
    }
  }

  return query;
}

const CourseSessionsAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<CourseSessionRow[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    userId: searchParams.get('userId') || '',
    userRole: (searchParams.get('userRole') as UserRoleFilter) || 'any',
  }));
  const [editSession, setEditSession] = useState<CourseSessionRow | null>(null);
  const [editForm, setEditForm] = useState({
    attendanceStatus: '',
    status: '',
    duration: '',
    teacherNotes: '',
    studentNotes: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const selectedUser = useMemo(
    () => users.find((u) => u._id === filters.userId) || null,
    [users, filters.userId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllUsers();
        if (!cancelled && res.data?.users) {
          setUsers(res.data.users);
        }
      } catch {
        /* user list is optional for filters */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncUserParams = useCallback(
    (userId: string, userRole: UserRoleFilter) => {
      const next = new URLSearchParams(searchParams);
      if (userId) {
        next.set('userId', userId);
        next.set('userRole', userRole);
      } else {
        next.delete('userId');
        next.delete('userRole');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAllCourseSessions(buildListFilters(page, filters));
      setSessions(res.data.sessions);
      setTotalPages(res.data.pagination.pages);
    } catch (e: any) {
      setError(e?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (s: CourseSessionRow) => {
    setEditSession(s);
    setEditForm({
      attendanceStatus: s.attendanceStatus || 'present',
      status: s.status || 'completed',
      duration: s.duration != null ? String(s.duration) : '',
      teacherNotes: s.teacherNotes || '',
      studentNotes: s.studentNotes || '',
    });
  };

  const handleSave = async () => {
    if (!editSession) return;
    setSaving(true);
    setMsg(null);
    try {
      await updateCourseSession(editSession._id, {
        attendanceStatus: editForm.attendanceStatus,
        status: editForm.status,
        duration: editForm.duration ? parseInt(editForm.duration, 10) : undefined,
        teacherNotes: editForm.teacherNotes,
        studentNotes: editForm.studentNotes,
      });
      setEditSession(null);
      setMsg('Session updated.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    try {
      await deleteCourseSession(id);
      setMsg('Session deleted.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Delete failed');
    }
  };

  const studentLabel = (s: CourseSessionRow) => {
    const u = s.userId;
    if (u && typeof u === 'object') return u.nickname || u.email || '—';
    return '—';
  };

  const teacherLabel = (s: CourseSessionRow) => {
    const p = s.profId;
    if (p && typeof p === 'object') return p.nickname || p.email || '—';
    return '—';
  };

  return (
    <ContentPageLayout>
    <Box>
      <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
        Course sessions
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Fleet-wide session list with filters. Edit attendance, notes, and status; delete when needed.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={users}
            value={selectedUser}
            onChange={(_e, user) => {
              const userId = user?._id || '';
              setPage(1);
              setFilters((f) => ({ ...f, userId }));
              syncUserParams(userId, filters.userRole);
            }}
            getOptionLabel={(u) => `${userDisplayName(u)} (${u.email})`}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderInput={(params) => <TextField {...params} label="User" placeholder="Search by name or email" />}
          />
          <TextField
            select
            size="small"
            label="User role"
            value={filters.userRole}
            disabled={!filters.userId}
            onChange={(e) => {
              const userRole = e.target.value as UserRoleFilter;
              setPage(1);
              setFilters((f) => ({ ...f, userRole }));
              if (filters.userId) {
                syncUserParams(filters.userId, userRole);
              }
            }}
            sx={{ minWidth: 160 }}
          >
            {USER_ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Attendance"
            value={filters.attendanceStatus}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, attendanceStatus: e.target.value })); }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            {ATTENDANCE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Type"
            value={filters.sessionType}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, sessionType: e.target.value })); }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            {SESSION_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            label="From"
            type="date"
            value={filters.startDate}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, startDate: e.target.value })); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={filters.endDate}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, endDate: e.target.value })); }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            size="small"
            onClick={() => {
              setPage(1);
              setFilters({ ...EMPTY_FILTERS });
              syncUserParams('', 'any');
            }}
          >
            Clear filters
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Attendance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">No sessions match filters.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s) => (
                  <TableRow key={s._id}>
                    <TableCell>{s.date ? new Date(s.date).toLocaleString() : '—'}</TableCell>
                    <TableCell>{studentLabel(s)}</TableCell>
                    <TableCell>{teacherLabel(s)}</TableCell>
                    <TableCell>{s.sessionType || '—'}</TableCell>
                    <TableCell>{s.attendanceStatus || '—'}</TableCell>
                    <TableCell>{s.status || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(s)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(s._id)} aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" gap={1} mt={2}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages || 1}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </Box>
        </>
      )}

      <Dialog open={!!editSession} onClose={() => setEditSession(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit session</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label="Attendance"
              value={editForm.attendanceStatus}
              onChange={(e) => setEditForm((f) => ({ ...f, attendanceStatus: e.target.value }))}
            >
              {ATTENDANCE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Status"
              value={editForm.status}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField
              color="primary"
              label="Duration (min)"
              type="number"
              value={editForm.duration}
              onChange={(e) => setEditForm((f) => ({ ...f, duration: e.target.value }))}
            />
            <TextField
              color="primary"
              label="Teacher notes"
              multiline
              minRows={2}
              value={editForm.teacherNotes}
              onChange={(e) => setEditForm((f) => ({ ...f, teacherNotes: e.target.value }))}
            />
            <TextField 
              color="primary"
              label="Student notes"
              multiline
              minRows={2}
              value={editForm.studentNotes}
              onChange={(e) => setEditForm((f) => ({ ...f, studentNotes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSession(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ContentPageLayout>
  );
};

export default CourseSessionsAdminPage;
